import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { VideoReview } from "../../../types";
import {
  VideoReviewFormProps,
  VideoReviewFormData,
} from "../types/video-reviews-types";
import { ControlledTextField } from "../../../components/form";
import { ControlledSelect } from "../../../components/form";
import { ControlledTextarea } from "../../../components/form";
import { ROLE_OPTIONS } from "../../../constants/forms";

export const VideoReviewForm: React.FC<VideoReviewFormProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
}) => {
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // Format date from ISO to YYYY-MM-DD for date input
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      // If the date string is already in YYYY-MM-DD format, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If it's an ISO string, extract the date portion directly without timezone conversion
      if (dateString.includes("T")) {
        return dateString.split("T")[0] || "";
      }
      // Otherwise, try to extract the first 10 characters (YYYY-MM-DD)
      return dateString.substring(0, 10) || "";
    } catch {
      return "";
    }
  };

  const form = useForm<VideoReviewFormData>({
    defaultValues: {
      customerName:
        initialValues?.name ||
        initialValues?.customerName ||
        initialValues?.title ||
        "",
      role: initialValues?.role || initialValues?.customerRole || "",
      reviewDate: formatDateForInput(
        initialValues?.date || initialValues?.reviewDate
      ),
      content: initialValues?.description || initialValues?.content || "",
      videoUrl: initialValues?.video_link || initialValues?.videoUrl || "",
      thumbnailUrl:
        initialValues?.image ||
        initialValues?.thumbnail ||
        initialValues?.thumbnailUrl ||
        "",
    },
  });

  useEffect(() => {
    if (initialValues) {
      const thumbnail =
        initialValues.image ||
        initialValues.thumbnail ||
        initialValues.thumbnailUrl;
      setThumbnailPreview(thumbnail || null);
    } else {
      setThumbnailPreview(null);
    }
  }, [initialValues]);

  // Watch the thumbnailUrl field for changes
  const thumbnailUrl = form.watch("thumbnailUrl");

  useEffect(() => {
    setThumbnailPreview(thumbnailUrl || null);
  }, [thumbnailUrl]);

  const handleFormSubmit = async (data: VideoReviewFormData): Promise<void> => {
    const reviewData: Partial<VideoReview> = {
      name: data.customerName,
      customerName: data.customerName,
      title: data.customerName,
      role: data.role,
      customerRole: data.role,
      date: data.reviewDate,
      reviewDate: data.reviewDate,
      description: data.content,
      content: data.content,
      video_link: data.videoUrl,
      videoUrl: data.videoUrl,
      image: data.thumbnailUrl || "",
      thumbnail: data.thumbnailUrl || "",
      thumbnailUrl: data.thumbnailUrl || "",
    };
    await onSubmit(reviewData);
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleFormSubmit)}
      className="form-space-y-2"
    >
      <ControlledTextField
        control={form.control}
        name="customerName"
        label="Имя клиента"
        placeholder="Введите имя клиента"
        required
        disabled={isLoading}
      />

      <ControlledSelect
        control={form.control}
        name="role"
        label="Роль"
        placeholder="Выберите роль"
        required
        disabled={isLoading}
        options={ROLE_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
      />

      <ControlledTextField
        control={form.control}
        name="reviewDate"
        type="date"
        label="Дата отзыва"
        placeholder="Выберите дату отзыва"
        required
        disabled={isLoading}
      />

      <ControlledTextarea
        control={form.control}
        name="content"
        label="Содержание отзыва"
        placeholder="Введите текст отзыва"
        required
        disabled={isLoading}
        rows={4}
      />

      <ControlledTextField
        control={form.control}
        name="videoUrl"
        type="url"
        label="Ссылка на видео"
        placeholder="Введите ссылку на видео"
        required
        disabled={isLoading}
      />

      <div className="form-group">
        <label htmlFor="thumbnailUrl">Ссылка на изображение</label>
        <input
          type="url"
          id="thumbnailUrl"
          className="form-input"
          placeholder="Введите ссылку на изображение"
          disabled={isLoading}
          {...form.register("thumbnailUrl")}
        />
      </div>

      {thumbnailPreview && (
        <div className="video-review__thumbnail-preview">
          <img
            src={thumbnailPreview}
            alt="Thumbnail preview"
            style={{
              maxWidth: "200px",
              maxHeight: "150px",
              marginTop: "10px",
              borderRadius: "4px",
            }}
          />
        </div>
      )}

      <div className="form-wrapper__actions">
        <button
          type="submit"
          className="form-button form-button--primary form-button--full"
          disabled={isLoading || !form.formState.isValid}
        >
          {isLoading
            ? "Сохранение..."
            : initialValues
            ? "Сохранить изменения"
            : "Добавить видеоотзыв"}
        </button>
      </div>
    </form>
  );
};
